import { FiCheck } from "react-icons/fi";
import { SelectWithLabel } from "newui/components/ui/InputWithLabel";
import { cn } from "utils/helpers";
import { useAssignableRoles } from "newui/features/RolesPermissions/hooks/useAssignableRoles";
import { scopeTypeStyle } from "newui/features/RolesPermissions";
import type { ScopeType } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { AccessInfoNotice } from "./AccessInfoNotice";

// Author item F7 — company-access.png shows the three specific-access scope-type icons on this
// card, not a single generic pin. Reuses the SAME SCOPE_TYPE_STYLE icon map `ScopePickerList.tsx`
// now renders per row, rather than declaring yet another icon choice for the same three types.
const SPECIFIC_ACCESS_ICON_TYPES: ScopeType[] = ["project_type", "project", "rfx"];

export type AccessMode = "global" | "specific";

export interface AccessModeSelectorProps {
	// 1..N target users — drives both the role dropdown's server-side
	// assignable_to_user_ids[] filter (CONTRACT §5, rule 5) and which "Add
	// unavailable" copy variant renders (rule 6).
	targetUserIds: number[];
	roleId: number | null;
	/**
	 * `roleName` rides alongside `roleId` (rather than the caller re-looking it up) so
	 * `AccessConfirmModal` can name the role being added (author item 5.2) without needing its
	 * own copy of the roles list — this component already has it from the same select.
	 */
	onRoleChange: (roleId: number | null, roleName: string | null) => void;
	mode: AccessMode;
	onModeChange: (mode: AccessMode) => void;
}

// data-testid string is frozen by CONTRACT §8 ("evaluation-mode-selector" — a misnomer
// inherited from an unrelated component the author copy-edited from) — keep the STRING,
// but the component itself is named AccessModeSelector, not EvaluationModeSelector.
const EVALUATION_MODE_SELECTOR_TESTID = "evaluation-mode-selector";

// Role select (CONTRACT §5/§8) + the Company-Wide-vs-Specific mode choice (rule set,
// A6's info copy) — the Add flow's first screen. Rule 6: when the server-filtered role
// list is empty for every target user, nothing is selectable and the "Add unavailable"
// copy renders instead of the cards.
export const AccessModeSelector = ({
	targetUserIds,
	roleId,
	onRoleChange,
	mode,
	onModeChange,
}: AccessModeSelectorProps) => {
	// PRO-730 — negative ids are tour-fixture dummies (see
	// guides/features/Auth/tourFixtures.ts's negative-id convention). The
	// server validates `assignable_to_user_ids[]` with `exists:users,id`
	// (CONTRACT §5), so sending a dummy's id 422s — which would break the
	// "Allocate roles to several users" guided tour, which opens this screen
	// for `TOUR_USER_ASSIGNMENT`. A fixture id never contributes a real
	// "assignable to" filter, so it's dropped before the request; an
	// all-dummy target set falls back to the unfiltered list (harmless for a
	// tour, since nothing here ever reaches a real mutation — see
	// AddAccessFlow's own id<0 guard on apply).
	const realTargetUserIds = targetUserIds.filter((id) => id >= 0);
	/**
	 * CONTRACT §5.1 ADDENDUM (author bug 3.2, P0) — the direct-add role picker must never offer
	 * an event-scoped role (Bid Approver/Bid Observer/Evaluator/RFx Lead/Moderator/Observer/
	 * Panel Member/RFx Approver): those are only ever granted per-RFx by panel assignment, and
	 * a company-wide or specific-scope grant of one 422s on apply (`rbac.privilege_clamped`)
	 * even though preview promised it.
	 *
	 * PRO-757 A2 — both halves of §5.1's deliberate defence in depth (the server-side
	 * `exclude_event_scoped` param and the client-side `is_event_scoped !== true` filter) moved
	 * into `useAssignableRoles`, unchanged, so that the Add BUTTON's own rule-6 verdict
	 * (`UserListPage`) and this screen's are the same question asked once, under one query key,
	 * instead of two questions that could disagree. Neither layer was dropped.
	 */
	const { roles: assignableRoles, isLoading } = useAssignableRoles(
		realTargetUserIds.length > 0 ? realTargetUserIds : undefined
	);

	const roleOptions = assignableRoles.map((role) => ({ label: role.name, value: role.id }));
	const selectedRoleOption = roleOptions.find((option) => option.value === roleId) ?? null;

	// Rule 6: Add is unavailable only once the union-filtered, event-scoped-excluded role list
	// has genuinely resolved to empty — never while it's still loading (that would flash the
	// wrong message for one frame on every open). Checked against `assignableRoles`, not `roles`
	// — a target with ONLY event-scoped roles left to grant must see "unavailable", not a
	// dropdown offering roles this flow can never actually assign.
	const noRolesAvailable = !isLoading && targetUserIds.length > 0 && assignableRoles.length === 0;

	if (noRolesAvailable) {
		const copy =
			targetUserIds.length === 1
				? "This user already has company-wide access to every available role"
				: "Every selected user already has company-wide access to every available role";

		return (
			<div
				className="rounded-md border border-gray-200 bg-gray-50 px-3.5 py-3 text-sm text-gray-600"
				data-testid="access-mode-no-roles-available"
			>
				{copy}
			</div>
		);
	}

	const GlobalIcon = scopeTypeStyle("global").icon;

	return (
		<div>
			<div className="mb-4 max-w-sm">
				<SelectWithLabel
					label="Role"
					required
					testId="role-select"
					options={roleOptions}
					value={selectedRoleOption}
					onChange={(option) =>
						option && !Array.isArray(option)
							? onRoleChange(option.value as number, option.label)
							: onRoleChange(null, null)
					}
					placeholder="Select a role…"
					isLoading={isLoading}
				/>
			</div>

			<p className="mb-2 text-[11.5px] font-bold uppercase tracking-wide text-gray-600">
				Access level
			</p>
			<div
				className="grid grid-cols-1 gap-3 sm:grid-cols-2"
				data-testid={EVALUATION_MODE_SELECTOR_TESTID}
				role="radiogroup"
				aria-label="Access level"
			>
				<button
					type="button"
					role="radio"
					aria-checked={mode === "global"}
					onClick={() => onModeChange("global")}
					data-testid="mode-card-manual_panel"
					className={cn(
						"relative flex flex-col items-start gap-1.5 rounded-lg border-2 p-4 text-left transition-colors",
						mode === "global"
							? "border-blue-500 bg-blue-50"
							: "border-gray-200 hover:border-blue-500 hover:shadow"
					)}
				>
					{mode === "global" && (
						<span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-white">
							<FiCheck size={12} />
						</span>
					)}
					<GlobalIcon
						size={20}
						className={cn(scopeTypeStyle("global").iconClassName, "text-gray-500")}
						aria-hidden="true"
					/>
					{/* C4: standalone label, unhyphenated "Company Wide Access" (matches company-access.png). */}
					<h4 className="text-sm font-semibold">Company Wide Access</h4>
					<p className="text-xs text-gray-600">
						Full access across every portfolio, project and RFx — now and in the future.
					</p>
				</button>

				<button
					type="button"
					role="radio"
					aria-checked={mode === "specific"}
					onClick={() => onModeChange("specific")}
					data-testid="mode-card-manual_solo"
					className={cn(
						"relative flex flex-col items-start gap-1.5 rounded-lg border-2 p-4 text-left transition-colors",
						mode === "specific"
							? "border-blue-500 bg-blue-50"
							: "border-gray-200 hover:border-blue-500 hover:shadow"
					)}
				>
					{mode === "specific" && (
						<span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-white">
							<FiCheck size={12} />
						</span>
					)}
					<div className="flex items-center gap-1.5" data-testid="mode-card-manual_solo-icons">
						{SPECIFIC_ACCESS_ICON_TYPES.map((type) => {
							const style = scopeTypeStyle(type);
							const TypeIcon = style.icon;
							return (
								<TypeIcon
									key={type}
									size={18}
									// Author retest 1.1/2.1 — style.iconClassName carries RFx's rotation (see
									// scopeTypeStyles.ts); empty string for the other two types.
									className={cn(style.iconClassName, style.rowSelectedText)}
									aria-hidden="true"
									data-testid={`mode-card-manual_solo-icon-${type}`}
								/>
							);
						})}
					</div>
					<h4 className="text-sm font-semibold">Specific Access</h4>
					<p className="text-xs text-gray-600">
						Pick exactly which portfolios, projects or RFx events this role applies to.
					</p>
				</button>
			</div>

			<div className="mt-4">
				<AccessInfoNotice mode={mode} />
			</div>
		</div>
	);
};
